/**
 * Auto-pick a quality tier based on device hints. Cheaper than
 * runtime FPS sampling and gives a stable experience from frame 0.
 *
 * - low:    no antialias, 1024 shadow map, basic shadows, pixelRatio = 1
 * - medium: AA, 1024 shadow map, soft shadows, pixelRatio capped at 1.5
 * - high:   AA, 2048 shadow map, soft shadows, pixelRatio capped at 2
 */
export type Quality = "low" | "medium" | "high";

export interface QualitySettings {
  antialias: boolean;
  shadowMapSize: number;
  softShadows: boolean;
  pixelRatioCap: number;
  hemiLightIntensity: number;
}

const TIERS: Record<Quality, QualitySettings> = {
  low: {
    antialias: false,
    shadowMapSize: 1024,
    softShadows: false,
    pixelRatioCap: 1,
    hemiLightIntensity: 0.65,
  },
  medium: {
    antialias: true,
    shadowMapSize: 1024,
    softShadows: true,
    pixelRatioCap: 1.5,
    hemiLightIntensity: 0.6,
  },
  high: {
    antialias: true,
    shadowMapSize: 2048,
    softShadows: true,
    pixelRatioCap: 2,
    hemiLightIntensity: 0.55,
  },
};

let cached: Quality | null = null;
const STORAGE_KEY = "wr_quality";

export function detectQuality(): Quality {
  if (cached) return cached;
  // Manual override (set by Settings UI later).
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Quality | null;
    if (saved && saved in TIERS) {
      cached = saved;
      return cached;
    }
  } catch {}
  // Heuristic: dpr × cores × memory hints.
  const dpr = window.devicePixelRatio || 1;
  const cores = (navigator as { hardwareConcurrency?: number }).hardwareConcurrency ?? 4;
  const memGB =
    (navigator as { deviceMemory?: number }).deviceMemory ??
    (cores >= 8 ? 8 : 4);
  // Mobile UA → favor low.
  const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  let score = 0;
  if (cores >= 8) score += 2;
  else if (cores >= 4) score += 1;
  if (memGB >= 8) score += 2;
  else if (memGB >= 4) score += 1;
  if (dpr >= 2) score += 1;
  if (mobile) score -= 2;

  cached = score >= 4 ? "high" : score >= 2 ? "medium" : "low";
  return cached;
}

export function setQuality(q: Quality) {
  cached = q;
  try {
    localStorage.setItem(STORAGE_KEY, q);
  } catch {}
}

export function getQualitySettings(q: Quality = detectQuality()): QualitySettings {
  return TIERS[q];
}
